import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, Action } from '@ngrx/store';
import { isEqual } from 'lodash-es';
import { Observable, of, from } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom, filter, mergeMap, take, takeUntil, toArray } from 'rxjs/operators';

import { DealComparisonActions } from './deal-comparison.actions';
import { Deal } from '../../features/deal/models/deal.model';
import { DealComparisonService } from '../../features/deal/services/deal-comparison.service';
import { DealService } from '../../features/deal/services/deal.service';
import { ActivityEvent } from '../../features/home/models/activity-event.model';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { transactionToDeal } from '../../features/shared/utils/transaction-to-deal';
import { createActivityEffect } from '../create-activity-effect';
import { DealFilterActions } from '../deal-filter/deal-filter.actions';
import { RootState } from '../root-state.model';
import { TransactionSelectors } from '../transaction/transaction.selectors';
import { UserProfileActions } from '../user-profile/user-profile.actions';

/** Matches the HTTP/1.1 per-host ceiling, so a fresh scope never bursts past what a browser would allow anyway. */
const DEAL_DOWNLOAD_CONCURRENCY = 6;

@Injectable()
export class DealComparisonEffects {
  private readonly dealService = inject(DealService);
  private readonly comparisonService = inject(DealComparisonService);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly actions$ = inject(Actions);

  setDownloadEnabledEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.SetDownloadEnabled),
      filter((action) => !!action.downloadEnabled),
      map(() => DealComparisonActions.DownloadDeals({}))
    )
  );

  setCompareEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.SetCompare),
      // Ids resolve through the transaction list, which a cold load is still fetching: resolving early drops them all.
      switchMap((action) =>
        this.store.select(TransactionSelectors.loadSettled).pipe(
          filter((isSettled) => isSettled),
          take(1),
          map(() => action),
        ),
      ),
      concatLatestFrom(() => [this.store, this.store.select(TransactionSelectors.listLookup)]),
      map(([action, store, transactionLookup]) => {
        // A deal fetched for the previous scope is reused, so adding one deal costs one request
        // rather than one per deal in the comparison. Anything still in `dealsToDownload` is
        // mid-flight or failed, and is requested again.
        const fetched = new Map(store.dealComparison.deals.map((deal) => [deal.transactionId, deal]));
        const pending = new Set(store.dealComparison.dealsToDownload);

        const deals = action.transactionIds.map((tId) => fetched.get(tId) ?? transactionLookup[tId]).filter((deal) => !!deal);

        return DealComparisonActions.SetCompareSuccess({
          deals,
          dealsToDownload: deals
            .filter((deal) => !fetched.has(deal.transactionId) || pending.has(deal.transactionId))
            .map((deal) => deal.transactionId),
        });
      })
    )
  );

  addToCompareSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.SetCompareSuccess),
      withLatestFrom(this.store),
      filter(([action, store]) => !!store.dealComparison.downloadEnabled),
      map(([action, store]) => DealComparisonActions.DownloadDeals({}))
    )
  );

  setDealFilterIdEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.SetDealFilterId),
      // An empty id would set `loading` with nothing to resolve it: `useFilterQueryEffect$` bails on
      // a falsy id, so no success or failure ever arrives and the flag sticks for the session.
      filter((action) => !!action.dealFilterId),
      map((action) => DealFilterActions.UseFilter({ id: action.dealFilterId }))
    )
  );

  downloadDealsEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.DownloadDeals),
      withLatestFrom(this.store, this.store.select(TransactionSelectors.listLookup)),
      switchMap(([action, store, transactionLookup]) => {
        const transactionIds = action.transactionIds || store.dealComparison.dealsToDownload;

        const transactionsToDownload = transactionIds
          .map((id) => transactionLookup[id])
          .filter((value) => !!value);

        if (!transactionsToDownload.length) {
          return of(
            DealComparisonActions.DownloadDealsSuccess({ deals: store.dealComparison.deals })
          );
        }

        return from(transactionsToDownload).pipe(
          mergeMap(
            (transaction) =>
              this.dealService.getByTransaction(transaction).pipe(
                map((res) => (Object.keys(res).length ? res : transactionToDeal(transaction))),
                catchError(() => of(transactionToDeal(transaction)))
              ),
            DEAL_DOWNLOAD_CONCURRENCY
          ),
          toArray(),
          // Cancels the requests and stops the stale batch landing: `ClearComparison` only empties
          // the store, and this effect's `deals` snapshot would otherwise put the old scope back.
          takeUntil(this.actions$.pipe(ofType(DealComparisonActions.ClearComparison))),
          map((deals: Deal[]) => {
            // Swapped in by id: `mergeMap` completes out of order, and the scope order set by
            // SetCompareSuccess is what the columns follow.
            const fetchedById = new Map(deals.map((deal) => [deal.transactionId, deal]));

            return DealComparisonActions.DownloadDealsSuccess({
              deals: store.dealComparison.deals.map((deal) => fetchedById.get(deal.transactionId) ?? deal),
            });
          }),
          reportEffectError(DealComparisonActions.DownloadDealsFail)
        );
      })
    )
  );

  downloadDealsSuccessEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.DownloadDealsSuccess),
      withLatestFrom(this.store),
      map(([action, store]) => DealComparisonActions.Compare())
    )
  );

  compareEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.Compare),
      withLatestFrom(this.store),
      map(([action, store]) =>
        DealComparisonActions.CompareSuccess({
          comparison: this.comparisonService.compareDeals(store.dealComparison.deals as Deal[]),
        })
      )
    )
  );

  toggleBasketEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DealComparisonActions.ToggleBasket),
      withLatestFrom(this.store),
      map(([action, store]) => {
        const basket = store.dealComparison.basket || [];

        return basket.includes(action.transactionId)
          ? DealComparisonActions.RemoveFromBasket({ transactionId: action.transactionId })
          : DealComparisonActions.AddToBasket({ transactionIds: [action.transactionId] });
      })
    )
  );

  syncBasketEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DealComparisonActions.AddToBasket,
        DealComparisonActions.RemoveFromBasket,
        DealComparisonActions.ReplaceBasket,
        DealComparisonActions.ClearBasket
      ),
      withLatestFrom(this.store),
      map(([action, store]) => {
        const basket = store.dealComparison.basket || [];

        return UserProfileActions.UpdateAssetSetting({
          setting: {
            basket,
          },
        });
      })
    )
  );

  refreshBasketEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.RefreshAssetSettingSuccess),
      withLatestFrom(this.store),
      filter(
        ([action, store]) =>
          !isEqual(action.assetSetting?.basket || [], store.dealComparison.basket)
      ),
      map(([action, store]) =>
        DealComparisonActions.SetBasket({ basket: action.assetSetting?.basket || [] })
      )
    )
  );

  addToBasket$ = createActivityEffect(
    this.actions$,
    DealComparisonActions.AddToBasket,
    ActivityEvent.SelectedDealsAdd
  );
  removeFromBasket$ = createActivityEffect(
    this.actions$,
    DealComparisonActions.RemoveFromBasket,
    ActivityEvent.SelectedDealsRemove
  );
  clearBasket$ = createActivityEffect(
    this.actions$,
    DealComparisonActions.ClearBasket,
    ActivityEvent.SelectedDealsRemove
  );
}
